import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { PageHeader, CtaBand } from "../components/site/Sections";
import { Reveal } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import { useCases } from "../lib/useCases";
import NotFound from "./NotFound";

export default function CaseStudy() {
  const { slug } = useParams();
  const { cases: CASES, loaded } = useCases();
  const idx = CASES.findIndex((c) => c.slug === slug);
  if (idx < 0 && !loaded) return <div className="wrap min-h-[60vh] pt-24"><div className="h-16 w-2/3 animate-pulse rounded-xl bg-muted" /></div>;
  if (idx < 0) return <NotFound />;
  const c = CASES[idx];
  const next = CASES[(idx + 1) % CASES.length];

  return (
    <>
      <Seo path={`/work/${c.slug}`} title={`${c.client} case study`} description={`${c.industry}: ${c.results}`} />
      <PageHeader eyebrow={c.industry} lines={[c.client]} intro={c.results}>
        <Reveal delay={0.3} className="mt-8">
          <Link to="/work" className="inline-flex items-center gap-2 text-sm font-semibold text-ink hover:text-flame" data-testid="back-to-work"><ArrowLeft className="h-4 w-4" /> All work</Link>
        </Reveal>
      </PageHeader>
      <section className="wrap">
        <motion.div initial={{ clipPath: "inset(12% 8% 12% 8% round 2rem)" }} whileInView={{ clipPath: "inset(0% 0% 0% 0% round 2rem)" }} viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }} className="aspect-[16/10] overflow-hidden rounded-[2rem] bg-muted sm:aspect-[21/9]">
          <img src={c.image} alt={`${c.industry} project`} className="h-full w-full object-cover" />
        </motion.div>
        <div className="mt-6 grid grid-cols-3 gap-3 sm:gap-5" data-testid="case-metrics">
          {(c.metrics || []).map(([n, l]) => (
            <div key={l} className="rounded-2xl border border-ink/10 bg-white p-5 sm:p-8">
              <p className="font-display text-2xl font-bold text-ink sm:text-5xl">{n}</p>
              <p className="mt-1 text-xs text-ink/65 sm:text-sm">{l}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="wrap section grid gap-12 md:grid-cols-3">
        {[["The challenge", c.challenge], ["Our approach", c.approach], ["The results", c.results]].map(([t, d], i) => (
          <Reveal key={t} delay={i * 0.1} className="border-t-2 border-ink pt-6">
            <span className="font-display text-sm font-semibold text-flame">0{i + 1}</span>
            <h2 className="mt-3 font-display text-2xl font-bold text-ink">{t}</h2>
            <p className="mt-3 leading-relaxed text-ink/75">{d}</p>
          </Reveal>
        ))}
      </section>
      <section className="wrap pb-20">
        <p className="text-sm text-ink/65">Services: {(c.services || []).join(" · ")}</p>
        <Link to={`/work/${next.slug}`} className="group mt-8 flex items-center justify-between border-y border-ink/10 py-10" data-testid="next-case">
          <div><p className="eyebrow">Next project</p><p className="mt-3 font-display text-3xl font-bold tracking-tight text-ink sm:text-5xl">{next.client}</p></div>
          <span className="grid h-14 w-14 place-items-center rounded-full bg-ink text-canvas transition-[transform,background-color] duration-500 group-hover:rotate-45 group-hover:bg-flame"><ArrowUpRight className="h-6 w-6" /></span>
        </Link>
      </section>
      <CtaBand />
    </>
  );
}
